import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  /* Import Inter font - World-class typography */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  /* CSS Reset and Base Styles */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;

    /* Support for user font size preferences */
    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
    }

    /* Larger font sizes for better readability on larger screens */
    @media (min-width: ${props => props.theme.breakpoints.desktop}) {
      font-size: 18px;
    }

    @media (min-width: ${props => props.theme.breakpoints.ultrawide}) {
      font-size: 20px;
    }
  }

  body {
    font-family: ${props => props.theme.fonts.primary};
    font-size: 1rem; /* 16px base - WCAG accessible */
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    line-height: 1.5; /* Optimal for readability */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-feature-settings: 'kern' 1, 'liga' 1; /* Better typography */
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.heading};
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 0.5em;
  }

  h1 {
    font-size: 2.25rem; /* 36px - Hero headings */
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.01em;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 1.875rem; /* 30px */
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 1.5rem; /* 24px */
    }
  }

  h2 {
    font-size: 1.875rem; /* 30px - Page headings */
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.01em;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 1.5rem; /* 24px */
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 1.25rem; /* 20px */
    }
  }

  h3 {
    font-size: 1.5rem; /* 24px - Section headings */
    font-weight: 600;
    line-height: 1.3;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 1.25rem; /* 20px */
    }
  }

  h4 {
    font-size: 1.25rem; /* 20px - Subsection headings */
    font-weight: 600;
    line-height: 1.4;
  }

  h5 {
    font-size: 1.125rem; /* 18px */
    font-weight: 600;
    line-height: 1.4;
  }

  h6 {
    font-size: 1rem; /* 16px */
    font-weight: 600;
    line-height: 1.5;
  }

  /* Text Elements */
  p {
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }


  /* Lists */
  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Enhanced focus styles for accessibility */
  *:focus {
    outline: 2px solid ${props => props.theme.colors.focus};
    outline-offset: 2px;
  }

  /* Remove default focus for mouse users, keep for keyboard users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Buttons with enhanced accessibility */
  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    transition: all ${props => props.theme.transitions.normal} ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: ${props => props.theme.minTouchTarget};
    min-width: ${props => props.theme.minTouchTarget};

    &:hover {
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background-color: ${props => props.theme.colors.disabled};
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
      border: 2px solid currentColor;
    }
  }

  /* Enhanced Form Elements */
  input, textarea, select {
    font-family: inherit;
    font-size: 1rem;
    padding: 0.75rem;
    border: 2px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    transition: border-color ${props => props.theme.transitions.normal} ease;
    background-color: ${props => props.theme.colors.background};
    min-height: ${props => props.theme.minTouchTarget};

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
    }

    &::placeholder {
      color: ${props => props.theme.colors.textSecondary};
      opacity: 0.8;
    }

    &:invalid {
      border-color: ${props => props.theme.colors.error};
    }

    &:disabled {
      background-color: ${props => props.theme.colors.surface};
      color: ${props => props.theme.colors.disabled};
      cursor: not-allowed;
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
      border-width: 3px;
    }

    /* Larger text on small screens for better mobile experience */
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: 1.125rem; /* Prevents zoom on iOS Safari */
    }
  }

  /* Utility Classes */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      padding: 0 0.75rem;
    }
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .hidden {
    display: none;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Loading Animation */
  .loading-spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid ${props => props.theme.colors.border};
    border-radius: 50%;
    border-top-color: ${props => props.theme.colors.primary};
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Card Styles */
  .card {
    background: ${props => props.theme.colors.background};
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid ${props => props.theme.colors.border};
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      transform: translateY(-2px);
    }
  }

  /* Badge Styles */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;

    &.badge-primary {
      background-color: ${props => props.theme.colors.primary}15;
      color: ${props => props.theme.colors.primary};
    }

    &.badge-secondary {
      background-color: ${props => props.theme.colors.secondary}15;
      color: ${props => props.theme.colors.secondary};
    }

    &.badge-success {
      background-color: ${props => props.theme.colors.success}15;
      color: ${props => props.theme.colors.success};
    }
  }

  /* Responsive Grid */
  .grid {
    display: grid;
    gap: 1.5rem;

    &.grid-1 { grid-template-columns: 1fr; }
    &.grid-2 { grid-template-columns: repeat(2, 1fr); }
    &.grid-3 { grid-template-columns: repeat(3, 1fr); }
    &.grid-4 { grid-template-columns: repeat(4, 1fr); }

    @media (max-width: ${props => props.theme.breakpoints.desktop}) {
      &.grid-4 { grid-template-columns: repeat(3, 1fr); }
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      &.grid-3, &.grid-4 { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      &.grid-2, &.grid-3, &.grid-4 { grid-template-columns: 1fr; }
    }
  }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surface};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.textSecondary}40;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.textSecondary}60;
  }

  /* Motion and Animation Preferences */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* High Contrast Mode Support */
  @media (prefers-contrast: high) {
    .card {
      border-width: 2px;
      border-color: currentColor;
    }

    .badge {
      border: 1px solid currentColor;
    }
  }

  /* Dark Mode Support (for future implementation) */
  @media (prefers-color-scheme: dark) {
    /* Future dark mode styles can be added here */
  }

  /* Age-appropriate link styling */
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.normal} ease;
    border-radius: 4px;
    padding: 2px 4px;
    margin: -2px -4px;

    &:hover {
      color: ${props => props.theme.colors.secondary};
      background-color: ${props => props.theme.colors.primary}10;
    }

    &:focus {
      background-color: ${props => props.theme.colors.focus}20;
    }
  }

  /* Skip to main content link for screen readers */
  .skip-link {
    position: absolute;
    top: -40px;
    left: 6px;
    background: ${props => props.theme.colors.primary};
    color: white;
    padding: 8px;
    text-decoration: none;
    border-radius: 4px;
    z-index: 1000;

    &:focus {
      top: 6px;
    }
  }

  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
    }

    a {
      text-decoration: underline;
    }

    .no-print {
      display: none !important;
    }
  }
`;

export default GlobalStyles;